/**
 * Regla pura del seguimiento de un proceso de timbrado en background.
 *
 * El backend guarda el progreso en una sola fila por descripción de proceso,
 * compartida entre corridas y sin id ni fechas expuestos. Mientras la tarea
 * nueva sigue en cola, esa fila conserva el estado final de la corrida anterior
 * (`procesando = false`). Por eso "terminado" solo se acepta si ya se vio el
 * proceso en curso, o si la fila cambió respecto a como estaba antes de lanzar.
 */

export const ESTADO_SEGUIMIENTO = Object.freeze({
  ESPERANDO_INICIO: 'esperando-inicio',
  EN_CURSO: 'en-curso',
  TERMINADO: 'terminado',
  NO_CONFIRMADO: 'no-confirmado',
});

// Fallas seguidas de la consulta de progreso tras las que se deja de dar seguimiento.
export const MAX_ERRORES_CONSECUTIVOS = 3;

/**
 * Cuenta las fallas consecutivas de la consulta de progreso: una respuesta
 * correcta reinicia la cuenta.
 * @param {number} consecutivos fallas seguidas antes de esta consulta
 * @param {boolean} huboError si esta consulta falló
 * @returns {{consecutivos: number, detener: boolean}}
 */
export const contarErrorSeguimiento = (consecutivos, huboError) => {
  const total = huboError ? consecutivos + 1 : 0;
  return { consecutivos: total, detener: total >= MAX_ERRORES_CONSECUTIVOS };
};

/**
 * Huella de la fila de proceso: los campos expuestos que cambian entre corridas.
 * @param {object|null|undefined} registro fila de ProcesoEnCursoTerminado
 * @returns {string|null} null si la fila no existe
 */
export const huellaRegistro = (registro) => (registro
  ? JSON.stringify([
    registro.procesando,
    registro.total_registros,
    registro.registro_en_curso,
    registro.resultado,
    registro.mensaje,
  ])
  : null);

/**
 * Decide en qué punto está el proceso lanzado.
 * @param {object} params
 * @param {{leido: boolean, huella: string|null}|null} params.previo fila antes de
 *   lanzar: `leido` false si no se pudo consultar; `huella` null si no existía
 * @param {object|null} params.registro fila actual (null si no existe)
 * @param {boolean} params.vistoEnCurso si alguna consulta anterior lo vio en curso
 * @param {boolean} params.vencido si se agotó el tiempo de seguimiento
 * @returns {string} uno de ESTADO_SEGUIMIENTO
 */
export const evaluarSeguimiento = ({
  previo, registro, vistoEnCurso, vencido,
}) => {
  let estado = ESTADO_SEGUIMIENTO.ESPERANDO_INICIO;
  if (registro?.procesando === true) {
    estado = ESTADO_SEGUIMIENTO.EN_CURSO;
  } else if (registro && (vistoEnCurso
    || (previo?.leido && huellaRegistro(registro) !== previo.huella))) {
    estado = ESTADO_SEGUIMIENTO.TERMINADO;
  }

  if (vencido && !vistoEnCurso && estado === ESTADO_SEGUIMIENTO.ESPERANDO_INICIO) {
    return ESTADO_SEGUIMIENTO.NO_CONFIRMADO;
  }
  return estado;
};

// Cómo terminó el seguimiento; define qué aviso ve el usuario.
export const DESENLACE = Object.freeze({
  ERRORES_CONSULTA: 'errores-consulta',
  ERRORES_PROCESO: 'errores-proceso',
  TERMINADO: 'terminado',
  INICIO_NO_CONFIRMADO: 'inicio-no-confirmado',
  SIGUE_EN_EJECUCION: 'sigue-en-ejecucion',
  ESPERA_ABANDONADA: 'espera-abandonada',
});

/**
 * Decide el desenlace al detener el seguimiento. El orden importa: las fallas de
 * consulta ganan (no hay dato confiable), luego los errores del proceso, luego el
 * término, y solo al final los casos sin confirmar.
 * @param {object} params
 * @param {object} params.progreso última respuesta de getProgresoProceso
 * @param {boolean} params.detenidoPorErrores si se detuvo por fallas consecutivas
 * @returns {string} uno de DESENLACE
 */
export const desenlaceSeguimiento = ({ progreso, detenidoPorErrores }) => {
  if (detenidoPorErrores) return DESENLACE.ERRORES_CONSULTA;
  if (progreso?.errores?.length) return DESENLACE.ERRORES_PROCESO;
  if (progreso?.terminado) return DESENLACE.TERMINADO;
  if (progreso?.inicioNoConfirmado) return DESENLACE.INICIO_NO_CONFIRMADO;
  return DESENLACE.SIGUE_EN_EJECUCION;
};
